import { Component, Input, Output, EventEmitter, OnInit, SimpleChanges } from '@angular/core';

import { VersionsService } from '@core/services/versions/versions.service';
import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionListItemMenuClick } from '@core/components/versions/list/version-list-item-menu-click';

import * as  Objects from '@core/objects';
import { VersionFeedback, FeedbackStatusColorValues, ApprovalStatusColorValues,FeedbackStatusCodeEnum } from '@core/models/version-feedback';
import { VersionShareItem, VersionShare } from '@core/models/version-share.model';
import { VersionSharesService } from '@core/services/version-shares/version-shares.service';
import { Approval } from '@core/objects/approval';

@Component({
    selector: 'versions-summary-status-box',
    templateUrl: './summary-status-box.component.html',
    styleUrls: ['./summary-status-box.component.scss']
})
export class VersionsSummaryStatusBoxComponent implements OnInit {
    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;

    public readonly APPROVAL_STATUS_COLORS = ApprovalStatusColorValues; //['#F6B273','#47B934','#F44848'];
    public readonly FEEDBACK_STATUS_COLORS = FeedbackStatusColorValues;
    
        //0-> Pending: F0BC36 (old'#F6B273',)
        //1-> Approved: 47B934 (old'#92D77D',)
        //2-> Rejected: F44848   (old'#C8494E', )
        //3-> only comments: '#C8494E', ????

    @Input() version: Objects.Version;
    @Input() uploaded: boolean = false;
    @Input() feedbacks: Array<VersionShareItem>;
    @Input() exclude: Array<Objects.Label> = [];
    @Input() menu: Array<ThreeDotsMenuItem> = [];
    @Input() filesPrefixUrl: string = "";
    @Input() listItemModeOnly: boolean = false;

    @Output() menuClick = new EventEmitter<VersionListItemMenuClick>();
    @Output() menuStatusClick = new EventEmitter<Objects.Status>();
    @Output() actionFileClick = new EventEmitter<Objects.AmsFile>();
    @Output() actionLabelClick = new EventEmitter<Objects.Label>();

    public status: number = this.STATUS_LOADING;
    public opened: boolean = false;
    public files: Array<Objects.AmsFile> = [];
    public diffLabels: Array<Objects.Label> = [];
    public feedbacksList: Array<VersionShareItem>;

    protected areLabelsObserved: boolean = false;
    
    constructor(private versionSrv: VersionsService, private urlSrv: UrlService, private fileDownloadSrv: FileDownloadService, private signatureManager: FileSignatureManagerService, private versionSharesSrv: VersionSharesService ) { }

    ngOnInit() {
        // check if there are observer on the Output.
        this.areLabelsObserved = this.actionLabelClick.observed;
        if(this.version != null)
            this.updateDiffLabels();
    }

    ngOnChanges(changes: SimpleChanges) {
        if(changes.feedbacks) {
            // get only unique feedback by each user when not null
            // oct-2026: one bubble per recipient and share type, so an Approval/Comments request and a
            // New Artwork Version request of the same recipient both show; exact duplicates still collapse
            this.feedbacksList = this.feedbacks!= null ? Array.from(new Set(this.feedbacks.map(a => this.getBubbleKey(a))))
                .map(id => {
                    return this.feedbacks.find(a => this.getBubbleKey(a) === id)
                }) : this.feedbacks;
        }

        if(changes.exclude)
            this.updateDiffLabels();
    }

    toogleFiles(event: any) {
        if(this.listItemModeOnly) //avoid open the files
            return;
        this.opened = !this.opened;
        // event.stopPropagation();

        if(this.status == this.STATUS_DONE)
            return;

        this.loadAssets();
    }

    onLabelSelect(tag: Objects.Label) {
        this.actionLabelClick.emit(tag);
    }

    onMenuSelect(key: string) {
        this.menuClick.emit({key: key, version: this.version});
    }

    onMenuStatusSelect(status: Objects.Status) {
        this.menuStatusClick.emit(status);
    }

    // download file directly
    onActionDownloadClick(file: Objects.AmsFile){
        // Use new signed file download if file has signature (access property)
        if (file.access) {
            // Check if signature is expired
            if (this.signatureManager.isSignatureExpired(file)) {
                // Signature expired, reload assets to get fresh signatures, then download
                this.loadAssets((freshAssets: Array<Objects.AmsFile>) => {
                    // Find the updated file with fresh signature
                    const updatedFile = freshAssets.find(f => f.id === file.id);
                    this.fileDownloadSrv.downloadVersionAsset(updatedFile || file);
                });
            } else {
                // Signature is valid, download directly
                this.fileDownloadSrv.downloadVersionAsset(file);
            }
        } else {
            // Fall back to legacy download method for files without signature
            window.location.href = this.urlSrv.build('/files/'+file.id);
        }
    }

    /**
     * Common method to load assets from version service
     * Updates this.files array and sets status to DONE
     * 
     * @param callback Optional callback to execute with fresh assets before status update
     */
    private loadAssets(callback?: (assets: Array<Objects.AmsFile>) => void): void {
        this.versionSrv.getAssets$(this.version, this.filesPrefixUrl).subscribe((assets: Array<Objects.AmsFile>) => {
            // Update files array with fresh signatures
            this.files = assets;
            
            // Execute optional callback before marking as done (e.g., for download after refresh)
            if (callback) {
                callback(assets);
            }
            
            this.status = this.STATUS_DONE;
        });
    }

    // @deprecated oct-2026: legacy share feedback bubbles (VersionFeedback), replaced by VersionShareItem
    // public getBubbleColor(feedback: VersionFeedback){
    //     let color;
    //     // fix for wrong status  or approval in feedbacks to set it yellow. <diego@twonas.com>
    //     if(feedback.approval == null && feedback.status == null){
    //         return this.FEEDBACK_STATUS_COLORS[FeedbackStatusCodeEnum.WRONG_CODE];
    //     }
    //     if (feedback.approval != null){
    //         color = this.APPROVAL_STATUS_COLORS[feedback.approval.status_code];
    //     } else {
    //         color = this.FEEDBACK_STATUS_COLORS[feedback.status.status_code];
    //     }
    //     return color;
    // }
    protected getBubbleKey(share: VersionShareItem): string {
        return share.entry.user.id + "|" + share.type;
    }

    public getBubbleTitle(share: VersionShareItem | VersionShare){
        return this.versionSharesSrv.getVersionShareBubbleTitle(share);
    }

    public getBubbleColor(feedback: VersionShareItem){
        return this.versionSharesSrv.getVersionShareBubbleColor(feedback);
    }

    private updateDiffLabels() {
        this.diffLabels = this.version.labels.filter((label: Objects.Label) => !this.exclude.some((l) => l.id == label.id));
    }

}
